export interface AccessGroupScope {
  groupId: number;
  groupName?: string | null;
  groupType: string;
  siteId: number;
  canManageQuota: boolean;
  canManageFreeCards: boolean;
  canDeleteCardReturnQuota: boolean;
  /** null = all clients */
  clientIds: number[] | null;
  subscriptionIds: number[];
  hasGroup?: boolean;
}

export interface AccessGroupListItem {
  id: number;
  name?: string | null;
  groupType: string;
  siteId: number;
  siteName?: string | null;
  active: boolean;
  canManageQuota: boolean;
  canManageFreeCards: boolean;
  canDeleteCardReturnQuota: boolean;
  userCount: number;
  clientCount: number;
  subscriptionCount: number;
  resUserIds: number[];
  clientIds: number[];
  subscriptionIds: number[];
}

export interface SaveAccessGroupPayload {
  userId: number;
  menuId: number;
  id?: number | null;
  name?: string | null;
  groupType: string;
  siteId: number;
  active: boolean;
  canManageQuota: boolean;
  canManageFreeCards: boolean;
  canDeleteCardReturnQuota: boolean;
  resUserIds: number[];
  clientIds: number[];
  subscriptionIds: number[];
}

export interface FreeParkingDashboard {
  scope: AccessGroupScope;
  siteQuotas: FreeParkingDashboardSiteQuota[];
  clientQuotas: FreeParkingDashboardClientQuota[];
  topClientsByCards: FreeParkingTopClientByCards[];
  activeFreeCardCount: number;
  nearFullAlerts: FreeParkingNearFullAlert[];
}

export interface FreeParkingDashboardSiteQuota {
  id: number;
  vehicleTypeId: number;
  vehicleTypeName?: string | null;
  quotaApproved: number;
  quotaUsed: number;
  quotaAllocatedToClients: number;
  remaining: number;
  status: string;
  usedPercent?: number;
}

export interface FreeParkingDashboardClientQuota {
  id: number;
  clientId: number;
  clientName?: string | null;
  vehicleTypeId: number;
  vehicleTypeName?: string | null;
  quotaApproved: number;
  quotaUsed: number;
  remaining: number;
}

export interface FreeParkingTopClientByCards {
  clientId: number;
  clientName?: string | null;
  cardCount: number;
  sharePercent: number;
}

export interface FreeParkingNearFullAlert {
  level: string;
  clientId?: number | null;
  label?: string | null;
  vehicleTypeId: number;
  vehicleTypeName?: string | null;
  remaining: number;
  quotaApproved: number;
  usedPercent: number;
}

export interface FreeParkingCardListItem {
  id: number;
  cardNo?: string | null;
  licensePlate?: string | null;
  clientId?: number | null;
  clientName?: string | null;
  subscriptionId?: number | null;
  subscriptionName?: string | null;
  vehicleTypeId?: number | null;
  vehicleTypeName?: string | null;
  expireDate?: string | null;
  status?: string | null;
}

export interface ValidateFreeParkingAdvancePayload {
  userId: number;
  menuId: number;
  siteId: number;
  clientId: number;
  subscriptionId: number;
  vehicleTypeId: number;
  qty: number;
  remark?: string | null;
}

export function createEmptyAccessGroupForm(siteId: number): SaveAccessGroupPayload {
  return {
    userId: 0,
    menuId: 0,
    id: null,
    name: '',
    groupType: 'building',
    siteId,
    active: true,
    canManageQuota: true,
    canManageFreeCards: true,
    canDeleteCardReturnQuota: false,
    resUserIds: [],
    clientIds: [],
    subscriptionIds: [],
  };
}

function camel<T>(raw: any): T {
  if (raw == null || typeof raw !== 'object') return raw as T;
  if (Array.isArray(raw)) return raw.map((x) => camel(x)) as T;
  const out: any = {};
  for (const [k, v] of Object.entries(raw)) {
    const ck = k.charAt(0).toLowerCase() + k.slice(1);
    out[ck] = v != null && typeof v === 'object' ? camel(v) : v;
  }
  return out as T;
}

export function mapAccessGroupList(res: any): AccessGroupListItem[] {
  const rows = Array.isArray(res) ? res : res?.data ?? [];
  return rows.map((r: any) => camel<AccessGroupListItem>(r));
}

export function mapAccessGroupScope(res: any): AccessGroupScope | null {
  if (!res) return null;
  const s = camel<AccessGroupScope>(res);
  if (!s.groupId) return null;
  return s;
}

export function mapFreeParkingDashboard(res: any): FreeParkingDashboard {
  const d = camel<FreeParkingDashboard>(res ?? {});
  return {
    scope: d.scope ?? ({} as AccessGroupScope),
    siteQuotas: d.siteQuotas ?? [],
    clientQuotas: d.clientQuotas ?? [],
    topClientsByCards: d.topClientsByCards ?? [],
    activeFreeCardCount: d.activeFreeCardCount ?? 0,
    nearFullAlerts: d.nearFullAlerts ?? [],
  };
}

export function mapFreeParkingCardList(res: any): FreeParkingCardListItem[] {
  const rows = Array.isArray(res) ? res : res?.data ?? [];
  return rows.map((r: any) => camel<FreeParkingCardListItem>(r));
}

/* ---------- Group categories ---------- */

export type AccessGroupCategory = 'building' | 'tenant';

export interface AccessGroupCategoryMeta {
  value: AccessGroupCategory;
  label: string;
  icon: string;
  description: string;
}

export const ACCESS_GROUP_CATEGORIES: AccessGroupCategoryMeta[] = [
  {
    value: 'building',
    label: 'ฝ่ายอาคาร',
    icon: 'apartment',
    description: 'ทีมอาคาร / นิติ ดูแลโควต้าและบัตรจอดฟรีของลาน',
  },
  {
    value: 'tenant',
    label: 'บริษัทผู้เช่า',
    icon: 'business_center',
    description: 'ตัวแทนบริษัทผู้เช่า จัดการบัตรจอดฟรีเฉพาะบริษัทของตัวเอง',
  },
];

export function accessGroupCategoryLabel(type: string | null | undefined): string {
  return ACCESS_GROUP_CATEGORIES.find((c) => c.value === type)?.label ?? 'ฝ่ายอาคาร';
}

/* ---------- Member pickers (server search + paging) ---------- */

export interface AccessGroupUserOption {
  id: number;
  login: string;
  displayName: string;
  roleName: string;
  /** active group at this site (null = free) */
  groupId: number | null;
  groupName: string;
}

export interface AccessGroupClientOption {
  id: number;
  name: string;
  taxId: string;
  contact: string;
  telephone: string;
}

export interface AccessGroupOptionPage<T> {
  data: T[];
  total: number;
  totalPages: number;
}

export interface AccessGroupMembers {
  users: AccessGroupUserOption[];
  clients: AccessGroupClientOption[];
}

export function mapAccessGroupUserOption(raw: any): AccessGroupUserOption {
  const r = camel<any>(raw ?? {});
  const id = Number(r.id ?? 0);
  const login = String(r.login ?? '').trim();
  return {
    id,
    login,
    displayName: String(r.displayName ?? '').trim() || login || `User #${id}`,
    roleName: r.roleName ?? '',
    groupId: r.groupId != null && Number(r.groupId) > 0 ? Number(r.groupId) : null,
    groupName: r.groupName ?? '',
  };
}

export function mapAccessGroupClientOption(raw: any): AccessGroupClientOption {
  const r = camel<any>(raw ?? {});
  const id = Number(r.id ?? 0);
  return {
    id,
    name: r.name || `บริษัท #${id}`,
    taxId: r.taxId ?? '',
    contact: r.contact ?? '',
    telephone: r.telephone ?? '',
  };
}

export function mapAccessGroupOptionPage<T>(res: any, mapper: (raw: any) => T): AccessGroupOptionPage<T> {
  const rows = Array.isArray(res?.data ?? res?.Data) ? (res.data ?? res.Data) : [];
  return {
    data: rows.map(mapper),
    total: Number(res?.total ?? res?.Total ?? 0),
    totalPages: Number(res?.totalPages ?? res?.TotalPages ?? 0),
  };
}

export function mapAccessGroupMembers(res: any): AccessGroupMembers {
  const users = res?.users ?? res?.Users ?? [];
  const clients = res?.clients ?? res?.Clients ?? [];
  return {
    users: Array.isArray(users) ? users.map(mapAccessGroupUserOption) : [],
    clients: Array.isArray(clients) ? clients.map(mapAccessGroupClientOption) : [],
  };
}
